import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import {
  PublicLocale,
  PUBLIC_LANGUAGE_KEY,
  publicRoute,
  resolvePublicLocale,
  localizedPath,
  localizedLoginPath,
} from './locales';
import { englishMessages, publicCatalogs, PublicMessages, translator } from './publicMessages';

export function languageValue(locale: PublicLocale, messages: PublicMessages) {
  return {
    locale,
    messages,
    t: translator(messages),
    href: (path: string) => localizedLoginPath(localizedPath(path, locale), locale),
  };
}
const PublicLanguage = createContext({
  ...languageValue('en', englishMessages),
  changeLanguage: (_locale: PublicLocale): void => undefined,
  loading: false,
  error: false,
});
export const usePublicLanguage = () => useContext(PublicLanguage);

/** Static rendering uses the same provider and copy as the client. */
export function StaticPublicLanguage({
  locale,
  messages,
  children,
}: {
  locale: PublicLocale;
  messages: PublicMessages;
  children: React.ReactNode;
}) {
  return (
    <PublicLanguage.Provider
      value={{
        ...languageValue(locale, messages),
        changeLanguage: () => undefined,
        loading: false,
        error: false,
      }}
    >
      {children}
    </PublicLanguage.Provider>
  );
}

function urlLocale(): PublicLocale {
  if (typeof window === 'undefined') {
    return 'en';
  }
  return publicRoute(window.location.pathname).locale ?? 'en';
}

export default function PublicLanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState(() =>
    languageValue(urlLocale(), publicCatalogs[urlLocale()]),
  );
  const [error, setError] = useState(false);
  const memoryChoice = useRef<PublicLocale>();

  const change = (locale: PublicLocale, manual: boolean, navigate: boolean) => {
    if (!publicCatalogs[locale]) {
      setError(true);
      return;
    }
    setError(false);
    if (manual) {
      memoryChoice.current = locale;
      try {
        localStorage.setItem(PUBLIC_LANGUAGE_KEY, locale);
      } catch {
        /* Visit still works. */
      }
    }
    setLanguage(languageValue(locale, publicCatalogs[locale]));
    if (navigate) {
      const { pathname, search, hash } = window.location;
      const destination = localizedPath(pathname, locale) + search + hash;
      if (destination !== pathname + search + hash) {
        window.history.replaceState(null, '', destination);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }
  };
  const changeRef = useRef(change);
  changeRef.current = change;
  useEffect(() => {
    let saved: string | null = null;
    let bible: string | undefined;
    try {
      saved = localStorage.getItem(PUBLIC_LANGUAGE_KEY);
    } catch {
      /* Optional storage. */
    }
    try {
      bible = Cookies.get('translation') || localStorage.getItem('translation') || undefined;
    } catch {
      /* Browser fallback. */
    }
    changeRef.current(
      resolvePublicLocale({
        pathname: window.location.pathname,
        saved,
        bible,
        browsers: navigator.languages || [navigator.language],
      }),
      !!publicRoute(window.location.pathname).locale,
      true,
    );
    const restore = () => {
      const locale = publicRoute(window.location.pathname).locale || memoryChoice.current || 'en';
      changeRef.current(locale, false, false);
    };
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);
  return (
    <PublicLanguage.Provider
      value={{
        ...language,
        changeLanguage: (locale: PublicLocale) => {
          change(locale, true, true);
        },
        loading: false,
        error,
      }}
    >
      {children}
    </PublicLanguage.Provider>
  );
}
